// Self-contained modal — opened from chat widget to capture a free 15-min discovery call request.
import { useState } from "react";
import api from "@/lib/api";
import { Phone, X, CheckCircle } from "@phosphor-icons/react";

const blank = { name: "", email: "", phone: "", preferred_day: "", topic: "", dog_age: "", consent_to_call: true };

export default function DiscoveryCallModal({ open, onClose }) {
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/discovery-calls", form);
      setSent(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Couldn't send your request — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => { onClose(); setSent(false); setForm(blank); };

  return (
    <div className="fixed inset-0 bg-stone-900/60 z-[60] flex items-start justify-center p-4 overflow-y-auto" onClick={close} data-testid="discovery-call-modal">
      <div className="bg-white rounded-2xl max-w-md w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-stone-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#f38838]/10 flex items-center justify-center"><Phone size={20} weight="duotone" className="text-[#f38838]" /></div>
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-[#f38838]">Free · 15 min</div>
              <h2 className="font-heading font-bold text-xl">Discovery Call</h2>
            </div>
          </div>
          <button onClick={close} className="text-stone-500 hover:text-stone-900"><X size={20} /></button>
        </div>
        {sent ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle size={28} weight="duotone" className="text-emerald-600" />
            </div>
            <h3 className="font-heading text-2xl font-bold text-stone-900 mb-2">Request received!</h3>
            <p className="text-stone-600 mb-6">the team will give you a ring between 9am–5pm on {form.preferred_day}. Keep your phone handy.</p>
            <button onClick={close} className="btn-primary">Sweet</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-3">
            <p className="text-sm text-stone-600">Pick a day that suits — the team will phone you between 9am and 5pm. We don't lock a specific time so calls don't get blocked.</p>
            <input data-testid="dc-name" required placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5" />
            <input data-testid="dc-email" required type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5" />
            <input data-testid="dc-phone" required type="tel" placeholder="Phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5" />
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-stone-500 block mb-1">Preferred day *</label>
              <input data-testid="dc-day" required type="date" value={form.preferred_day} onChange={(e) => setForm({ ...form, preferred_day: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5" />
            </div>
            <input type="text" placeholder="Dog age (optional)" value={form.dog_age} onChange={(e) => setForm({ ...form, dog_age: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5" />
            <textarea data-testid="dc-topic" rows={3} placeholder="What would you like to chat about?" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5" />
            <label className="flex items-start gap-2 text-xs text-stone-600 cursor-pointer">
              <input type="checkbox" required checked={form.consent_to_call} onChange={(e) => setForm({ ...form, consent_to_call: e.target.checked })} className="w-4 h-4 mt-0.5 accent-[#f38838]" />
              I'm happy for the team to call me between 9am–5pm on the preferred day.
            </label>
            <button data-testid="dc-submit" disabled={busy} className="btn-primary w-full disabled:opacity-50">{busy ? "Sending…" : "Request my free call"}</button>
          </form>
        )}
      </div>
    </div>
  );
}
